import { useState, useMemo, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import { authorizedFetch } from "@/lib/api"
import { SiteHeader } from "@/components/site-header"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  TruckIcon,
  PackageIcon,
  WrenchIcon,
  UserXIcon,
  CheckCircleIcon,
  ClockIcon,
  RouteIcon,
  UsersIcon,
  BuildingIcon,
  RefreshCwIcon,
  InfoIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

// ── Types ────────────────────────────────────────────────────────────────────

type WorkLineStat = {
  withActiveTrip: number
  withCargo:      number
  idle:           number
  inRepair:       number
  driverOff:      number
  total:          number
}

type FleetStats = {
  mobiles: {
    available:      number
    alistado:       number
    incomplete:     number
    withActiveTrip: number
    withCargo:      number
    idle:           number
    inRepair:       number
    driverOff:      number
  }
  byWorkLine: {
    batea:         WorkLineStat
    nacional:      WorkLineStat
    internacional: WorkLineStat
  }
}

type KpiStats = {
  trips: {
    total:       number
    withCargo:   number
    totalKm:     number
    withCargoKm: number
  }
  driverRanking: Array<{
    driverId:   string
    driverName: string
    tripCount:  number
    totalKm:    number
  }>
  clientRanking: Array<{
    clientId:   string
    clientName: string
    tripCount:  number
  }>
}

type WorkLineFilter = "all" | "batea" | "nacional" | "internacional"

// ── Helpers ──────────────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function firstOfMonthStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`
}

// ── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  info,
  icon: Icon,
  variant = "default",
  isLoading = false,
}: {
  label:     string
  value:     number | string
  sub?:      string | undefined
  info?:     string | undefined
  icon:      React.ElementType
  variant?:  "default" | "green" | "blue" | "amber" | "red" | "violet"
  isLoading?: boolean
}) {
  const colorMap = {
    default: "text-foreground",
    green:   "text-emerald-600",
    blue:    "text-blue-600",
    amber:   "text-amber-600",
    red:     "text-destructive",
    violet:  "text-violet-600",
  }
  const bgMap = {
    default: "bg-muted/40",
    green:   "bg-emerald-50 dark:bg-emerald-950/30",
    blue:    "bg-blue-50 dark:bg-blue-950/30",
    amber:   "bg-amber-50 dark:bg-amber-950/30",
    red:     "bg-red-50 dark:bg-red-950/30",
    violet:  "bg-violet-50 dark:bg-violet-950/30",
  }

  return (
    <Card className={cn("border", bgMap[variant])}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <CardDescription className="text-xs font-medium">{label}</CardDescription>
            {info && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="text-muted-foreground/40 hover:text-muted-foreground transition-colors focus:outline-none">
                    <InfoIcon className="size-3" />
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-56 text-xs">
                    {info}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <Icon className={cn("size-4 shrink-0", colorMap[variant])} />
        </div>
        <CardTitle className={cn("text-2xl font-bold tabular-nums", colorMap[variant])}>
          {isLoading ? "—" : typeof value === "number" ? value.toLocaleString("es-AR") : value}
        </CardTitle>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardHeader>
    </Card>
  )
}

function WorkLineTable({
  data,
  isLoading,
}: {
  data: { batea: WorkLineStat; nacional: WorkLineStat; internacional: WorkLineStat } | undefined
  isLoading: boolean
}) {
  const rows: { label: string; key: keyof WorkLineStat; info: string }[] = [
    { label: "Total móviles",    key: "total",          info: "Cantidad total de móviles no eliminados asignados a esa línea de trabajo." },
    { label: "Con viaje activo", key: "withActiveTrip", info: "Móviles con al menos un viaje en estado \"en curso\" en este momento." },
    { label: "Con carga",        key: "withCargo",      info: "Móviles con un viaje activo que tiene un envío asociado (carga real)." },
    { label: "Disponibles",      key: "idle",           info: "Móviles completamente armados (chofer activo + camión activo + semi activo) sin ningún viaje activo en este momento." },
    { label: "En taller",        key: "inRepair",       info: "Móviles donde el camión o el semi tiene estado \"en reparación\"." },
    { label: "Chofer de franco", key: "driverOff",      info: "Móviles cuyo chofer tiene un estado distinto a activo: enfermo, vacaciones, ART o suspendido." },
  ]

  const lines: { key: "batea" | "nacional" | "internacional"; label: string }[] = [
    { key: "batea",         label: "Batea" },
    { key: "nacional",      label: "Nacional" },
    { key: "internacional", label: "Internacional" },
  ]

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground w-52">Indicador</th>
                {lines.map(l => (
                  <th key={l.key} className="text-right px-4 py-3 font-medium text-muted-foreground">{l.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, key }, idx) => (
                <tr key={key} className={cn("border-b last:border-0", idx % 2 === 0 ? "" : "bg-muted/20")}>
                  <td className="px-4 py-3 text-muted-foreground">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger render={
                          <span className="flex items-center gap-1 cursor-default w-fit" />
                        }>
                          {label}
                          <InfoIcon className="size-3 text-muted-foreground/35 shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="right" align="center" className="max-w-60 text-xs">
                          {rows.find(r => r.label === label)?.info}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  {lines.map(l => {
                    const val = data?.[l.key]?.[key] ?? 0
                    const total = data?.[l.key]?.total ?? 1
                    const pct = total > 0 && key !== "total" ? Math.round((val / total) * 100) : null
                    return (
                      <td key={l.key} className="px-4 py-3 text-right tabular-nums font-semibold">
                        {isLoading ? "—" : (
                          <span>
                            {val.toLocaleString("es-AR")}
                            {pct !== null && (
                              <span className="ml-1 text-xs font-normal text-muted-foreground">({pct}%)</span>
                            )}
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Tab 1 — Fleet availability ────────────────────────────────────────────────

function FleetTab() {
  const { data: stats, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["kpi", "fleet-stats"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/dashboard/fleet-stats`)
      return res.json() as Promise<FleetStats>
    },
    staleTime: 60_000,
  })

  const m = stats?.mobiles

  return (
    <div className="flex flex-col gap-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Foto en tiempo real del estado de la flota. Actualizá para ver el estado más reciente.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <RefreshCwIcon className={cn("size-3.5", isFetching && "animate-spin")} />
          Actualizar
        </button>
      </div>

      {/* Global summary cards */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Resumen global — todos los móviles
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <KpiCard
            label="Con carga"
            value={m?.withCargo ?? 0}
            icon={PackageIcon}
            variant="green"
            isLoading={isLoading}
            sub="Viaje activo + envío"
            info="Móviles con un viaje en estado 'en curso' que tiene un envío asociado. Representa la flota efectivamente transportando carga en este momento."
          />
          <KpiCard
            label="Con viaje"
            value={m?.withActiveTrip ?? 0}
            icon={TruckIcon}
            variant="blue"
            isLoading={isLoading}
            sub="Trip en curso"
            info="Móviles con al menos un viaje en estado 'en curso', con o sin envío asociado (incluye viajes de interconexión y retorno a base)."
          />
          <KpiCard
            label="Disponibles"
            value={m?.idle ?? 0}
            icon={CheckCircleIcon}
            variant="default"
            isLoading={isLoading}
            sub="Sin viaje activo"
            info="Móviles completamente armados (chofer activo + camión activo + semi activo) que no tienen ningún viaje en curso en este momento."
          />
          <KpiCard
            label="En taller"
            value={m?.inRepair ?? 0}
            icon={WrenchIcon}
            variant="amber"
            isLoading={isLoading}
            sub="Unidad en reparación"
            info="Móviles donde el camión o el semi tiene estado 'en reparación', independientemente del estado del chofer."
          />
          <KpiCard
            label="Chofer de franco"
            value={m?.driverOff ?? 0}
            icon={UserXIcon}
            variant="red"
            isLoading={isLoading}
            sub="Sick / vacaciones / ART"
            info="Móviles cuyo chofer tiene un estado distinto a activo: enfermo, de vacaciones, en ART o suspendido."
          />
        </div>
      </div>

      {/* By work line */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Desglose por línea de trabajo
        </h3>
        <WorkLineTable data={stats?.byWorkLine} isLoading={isLoading} />
      </div>
    </div>
  )
}

// ── Paginated ranking tables ────────────────────────────────────────────────

const PAGE_SIZE = 10

function RankingPaginator({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number
  totalPages: number
  total: number
  onPage: (p: number) => void
}) {
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
      <span>{total} resultados</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
          className="rounded px-2 py-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          ‹
        </button>
        <span className="px-1 tabular-nums">{page} / {totalPages}</span>
        <button
          type="button"
          disabled={page === totalPages}
          onClick={() => onPage(page + 1)}
          className="rounded px-2 py-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          ›
        </button>
      </div>
    </div>
  )
}

function DriverRankingTable({
  data,
  isLoading,
}: {
  data: KpiStats["driverRanking"] | undefined
  isLoading: boolean
}) {
  const [page, setPage] = useState(1)
  const total      = data?.length ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const paged      = (data ?? []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const offset     = (page - 1) * PAGE_SIZE

  // Reset to page 1 when data changes
  const handlePage = useCallback((p: number) => setPage(p), [])

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
        <UsersIcon className="size-3.5" />
        Ranking choferes — km recorridos
      </h3>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : !data?.length ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Sin datos para el período</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs w-8">#</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Chofer</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Viajes</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Km</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((d, idx) => (
                    <tr key={d.driverId} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 text-muted-foreground text-xs tabular-nums">{offset + idx + 1}</td>
                      <td className="px-4 py-2.5 font-medium">{d.driverName}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{d.tripCount.toLocaleString("es-AR")}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-blue-600">
                        {d.totalKm.toLocaleString("es-AR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <RankingPaginator page={page} totalPages={totalPages} total={total} onPage={handlePage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ClientRankingTable({
  data,
  isLoading,
}: {
  data: KpiStats["clientRanking"] | undefined
  isLoading: boolean
}) {
  const [page, setPage] = useState(1)
  const total      = data?.length ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const paged      = (data ?? []).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const offset     = (page - 1) * PAGE_SIZE

  const handlePage = useCallback((p: number) => setPage(p), [])

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
        <BuildingIcon className="size-3.5" />
        Ranking clientes — viajes con carga
      </h3>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : !data?.length ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Sin datos para el período</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs w-8">#</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Cliente</th>
                    <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Viajes</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((cl, idx) => (
                    <tr key={cl.clientId} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 text-muted-foreground text-xs tabular-nums">{offset + idx + 1}</td>
                      <td className="px-4 py-2.5 font-medium">{cl.clientName}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-600">
                        {cl.tripCount.toLocaleString("es-AR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <RankingPaginator page={page} totalPages={totalPages} total={total} onPage={handlePage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ── Tab 2 — Accumulated KPIs ──────────────────────────────────────────────────

function AccumulatedTab() {
  const [from, setFrom]           = useState(firstOfMonthStr())
  const [to,   setTo]             = useState(todayStr())
  const [workLine, setWorkLine]   = useState<WorkLineFilter>("all")

  const queryParams = useMemo(() => {
    const p = new URLSearchParams({ from, to })
    if (workLine !== "all") p.set("workLine", workLine)
    return p.toString()
  }, [from, to, workLine])

  const { data, isLoading } = useQuery({
    queryKey: ["kpi", "kpi-stats", from, to, workLine],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/dashboard/kpi-stats?${queryParams}`)
      return res.json() as Promise<KpiStats>
    },
    enabled: !!from && !!to,
    staleTime: 60_000,
  })

  const trips = data?.trips

  return (
    <div className="flex flex-col gap-6">

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-muted/40 px-4 py-3">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            className="h-8 w-36 text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            className="h-8 w-36 text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Línea de trabajo</Label>
          <Select value={workLine} onValueChange={v => setWorkLine(v as WorkLineFilter)}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue>
                {workLine === "all" ? "Todas" : workLine.charAt(0).toUpperCase() + workLine.slice(1)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="batea">Batea</SelectItem>
              <SelectItem value="nacional">Nacional</SelectItem>
              <SelectItem value="internacional">Internacional</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Trips summary */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Viajes del período
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiCard
            label="Total viajes"
            value={trips?.total ?? 0}
            icon={TruckIcon}
            variant="default"
            isLoading={isLoading}
          />
          <KpiCard
            label="Viajes con carga"
            value={trips?.withCargo ?? 0}
            icon={PackageIcon}
            variant="green"
            isLoading={isLoading}
            sub={trips && trips.total > 0 ? `${Math.round((trips.withCargo / trips.total) * 100)}% del total` : undefined}
          />
          <KpiCard
            label="Km totales"
            value={trips?.totalKm ?? 0}
            icon={RouteIcon}
            variant="blue"
            isLoading={isLoading}
          />
          <KpiCard
            label="Km con carga"
            value={trips?.withCargoKm ?? 0}
            icon={RouteIcon}
            variant="violet"
            isLoading={isLoading}
            sub={trips && trips.totalKm > 0 ? `${Math.round((trips.withCargoKm / trips.totalKm) * 100)}% del total` : undefined}
          />
        </div>
      </div>

      {/* Rankings side by side */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DriverRankingTable data={data?.driverRanking} isLoading={isLoading} />
        <ClientRankingTable data={data?.clientRanking} isLoading={isLoading} />
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function KpiPage() {
  return (
    <>
      <SiteHeader title="KPI" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <Tabs defaultValue="fleet" className="flex flex-col gap-6">
          <TabsList className="w-fit">
            <TabsTrigger value="fleet" className="gap-1.5">
              <TruckIcon className="size-3.5" />
              Disponibilidad de flota
            </TabsTrigger>
            <TabsTrigger value="accumulated" className="gap-1.5">
              <ClockIcon className="size-3.5" />
              Acumulados
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fleet">
            <FleetTab />
          </TabsContent>

          <TabsContent value="accumulated">
            <AccumulatedTab />
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
